import type { ServicePageData } from './types';

const carpetMats: ServicePageData = {
  slug: 'carpet-mats',
  eyebrow: 'Display',
  eyebrowTo: '/services/all-display',
  title: 'Put your brand',
  titleAccent: 'underfoot.',
  intro:
    'Printed carpet and entrance mats bring your logo or message onto the floor, whether that is welcoming visitors through the door, marking out an exhibition stand or protecting a gym floor. Made to your size, in-house.',
  heroImages: [
    '/home/tablecloths/tablecloths-conference-studio.jpg',
    '/home/tablecloths/tablecloths-box-fitted-studio.jpg',
    '/home/tablecloths/tablecloths-journey.jpg',
    '/home/tablecloths/tablecloths-copper-beech.jpg',
    '/home/tablecloths/tablecloths-reptile-shed.jpg',
    '/home/tablecloths/tablecloths-runner-studio.jpg',
    '/home/gazebos/barkavenue-gazebo.jpg',
    '/home/gazebos/curry-king-gazebo.jpg',
    '/home/gazebos/wildjac-gazebo.jpg',
    '/home/gazebos/mayfield-gazebo.jpg',
    '/home/services/tablecloths.jpg',
    '/home/services/gazebos.jpg',
    '/home/tablecloths/tablecloths-conference-studio.jpg',
    '/home/gazebos/barkavenue-gazebo.jpg',
  ],

  useCases: [
    { title: 'Shop and office entrances', text: 'A branded welcome mat that greets every visitor at the door.' },
    { title: 'Exhibitions and trade stands', text: 'Mark out your stand and reinforce your brand at floor level.' },
    { title: 'Receptions', text: 'A polished first impression that matches the rest of your signage.' },
    { title: 'Gyms and studios', text: 'Durable rubber-backed mats that stand up to regular footfall.' },
    { title: 'Showrooms', text: 'Define a space or highlight a product area without permanent flooring work.' },
  ],

  optionsHeading: 'Choose your mat',
  optionsIntro: 'Printed mats built for the amount of footfall your space gets, from a light entrance mat to a heavy-duty exhibition floor.',
  options: [
    {
      id: 'logo-entrance-mats',
      title: 'Logo entrance mats',
      lead: 'A branded mat for the door, sized to fit your entrance.',
      body: 'Your logo printed full colour onto a low-profile mat with a non-slip rubber back, so it sits flat and stays put on most hard floors.',
      image: '/home/tablecloths/tablecloths-conference-studio.jpg',
      specs: [
        { label: 'Size', value: 'Standard sizes from 60 x 90cm, custom sizes on request.' },
        { label: 'Material', value: 'Printed carpet-pile top, rubber backing.' },
        { label: 'Finish', value: 'Full colour print, bound edges.' },
      ],
    },
    {
      id: 'exhibition-floor-mats',
      title: 'Exhibition floor mats',
      lead: 'Cover a whole stand footprint or just mark the entrance.',
      body: 'Supplied as tiles or a single large mat depending on the stand size, so your branding continues from the walls right down to the floor.',
      image: '/home/gazebos/barkavenue-gazebo.jpg',
      specs: [
        { label: 'Size', value: 'Made to your stand footprint, tiled for larger areas.' },
        { label: 'Material', value: 'Printed carpet tile or roll, rubber-backed.' },
        { label: 'Fixings', value: 'Double-sided tape or interlocking edges, subject to venue rules.' },
      ],
    },
    {
      id: 'rubber-backed-runners',
      title: 'Rubber-backed runners',
      lead: 'A longer mat for corridors, aisles and walkways.',
      body: 'The same printed finish as our standard mats, made as a runner so a single length of branding can guide visitors through your space.',
      image: '/home/tablecloths/tablecloths-runner-studio.jpg',
      specs: [
        { label: 'Size', value: 'Custom lengths up to several metres, standard widths.' },
        { label: 'Material', value: 'Printed carpet-pile top, rubber backing.' },
        { label: 'Finish', value: 'Full colour print, bound edges.' },
      ],
    },
    {
      id: 'gym-studio-mats',
      title: 'Gym and studio mats',
      lead: 'A heavier-duty mat built to handle regular equipment and footfall.',
      body: 'A tougher backing and finish than our standard entrance mats, aimed at spaces that see daily wear rather than occasional visitors.',
      image: '/home/tablecloths/tablecloths-box-fitted-studio.jpg',
      specs: [
        { label: 'Size', value: 'Standard and custom sizes to suit the space.' },
        { label: 'Material', value: 'Heavy-duty printed top, reinforced rubber backing.' },
        { label: 'Finish', value: 'Full colour print, bound or welded edges.' },
      ],
    },
  ],

  customisation: [
    {
      title: 'Full-colour print',
      text: 'Your logo, artwork or photography printed directly into the mat surface for a finish that reads clearly from a distance.',
    },
    {
      title: 'Cut vinyl overlay',
      text: 'A simpler logo or lettering applied as a durable vinyl layer, a cost-effective option for a single-colour design.',
    },
  ],

  gallery: [
    '/home/tablecloths/tablecloths-conference-studio.jpg',
    '/home/tablecloths/tablecloths-box-fitted-studio.jpg',
    '/home/tablecloths/tablecloths-journey.jpg',
    '/home/tablecloths/tablecloths-copper-beech.jpg',
    '/home/tablecloths/tablecloths-reptile-shed.jpg',
    '/home/tablecloths/tablecloths-runner-studio.jpg',
    '/home/gazebos/curry-king-gazebo.jpg',
  ],

  info: [
    {
      title: 'Supply only',
      text: 'Mats are supplied ready to lay, with no fitting required for standard sizes.',
      bullets: ['Collect from Kidderminster', 'Delivery from £10'],
    },
    {
      title: 'Care',
      text: 'Vacuum regularly and spot clean spills promptly. Rubber backing is designed for most hard floor types.',
    },
    {
      title: 'Turnaround',
      text: 'Production usually takes 5 to 10 working days from approval. Got a deadline? Talk to us and we will do our best.',
    },
  ],

  faqs: [
    {
      q: 'Will the mat be safe on our floor type?',
      a: 'The rubber backing suits most hard floors. Let us know your floor type and footfall when you enquire and we will confirm the right backing.',
    },
    {
      q: 'Can I get a mat to fit an unusual exhibition stand shape?',
      a: 'Yes, mats can be cut and tiled to fit most stand footprints. Send us your dimensions and we will work out the best layout.',
    },
  ],

  cta: {
    title: 'Get your free carpet mat quote.',
    text: 'Tell us the size and location and we will recommend the right mat and finish.',
    image: '/home/services/tablecloths.jpg',
  },
};

export default carpetMats;
